import {Observable, of, pipe, Subject} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {DEFAULT_AGGREGATION_SCALE} from './aggregation';
import {MdSubscriptionSubject, SecurityMarketData} from './md-subscription-subject';
import {vwapSizeToStr} from './vwap-utils';

enum PrivateVwapMdWebSocketMessageType {
    PRIVATE_VWAP_MD_SNAPSHOT = 'PRIVATE_VWAP_MD_SNAPSHOT'
}

interface PrivateVwapMdWebSocketMessage extends WebSocketMessage {
    messageType: PrivateVwapMdWebSocketMessageType.PRIVATE_VWAP_MD_SNAPSHOT;
    data: ArrayEncodedMdMessage[];
}

export class PrivateVwapMdSubscriptionSubject extends MdSubscriptionSubject {

    private static readonly sharedActualizeUnsubscription$ = new Subject<void>();

    public messageType: string;

    private readonly isReadyToCreateSubjectType: boolean;

    constructor(
        private vwapSize: number,
        protected override securityService: SecurityService
    ) {
        super(DEFAULT_AGGREGATION_SCALE, securityService);

        // Skip early calling createSubjectType from super class
        this.isReadyToCreateSubjectType = true;

        this.createSubjectType();
    }

    protected override createSubjectType(): void {

        // Skip early calling from super class
        if (this.isReadyToCreateSubjectType) {
            const vwapSuffix = this.vwapSize ? `#${vwapSizeToStr(this.vwapSize)}` : '';
            const messageType = `${PrivateVwapMdWebSocketMessageType.PRIVATE_VWAP_MD_SNAPSHOT}${vwapSuffix}`;

            this.messageType = messageType;

            this.type = {
                name: `PRIVATE_VWAP_MD${vwapSuffix}`,
                isMultiSubject: true,
                operations: {
                    SUBSCRIBE: `SUBSCRIBE_PRIVATE_VWAP_MD${vwapSuffix}`,
                    UNSUBSCRIBE: `UNSUBSCRIBE_PRIVATE_VWAP_MD${vwapSuffix}`
                },
                messagePipe: pipe(
                    filter((webSocketMessage: WebSocketMessage) => webSocketMessage.messageType === messageType),
                    mergeMap((mdWebSocketMessage: PrivateVwapMdWebSocketMessage): Observable<SecurityMarketData> => {
                        const mdMessageContainers = this.readPrivateMdWebSocketMessage(mdWebSocketMessage);

                        return of(...mdMessageContainers);
                    })
                ),
                sharedActualizeUnsubscription$: PrivateVwapMdSubscriptionSubject.sharedActualizeUnsubscription$,
                subjectPipeFactory: subject => this.createSubjectPipe(subject),
                deprecateSubject: subject => this.deprecateSubject(subject)
            };
        }
    }

    protected readPrivateMdWebSocketMessage(mdWebSocketMessage: PrivateVwapMdWebSocketMessage): SecurityMarketData[] {
        return this.readMdSnapshot(mdWebSocketMessage.data as ArrayEncodedMdSnapshotMessage[]);
    }

}
